import { styled } from '@mui/material'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import { getWbsItemStatusLabel } from '../../../../lib/functions/wbsItem'
import { colorPalette } from '../../../style/colorPalette'

type ChipStyle = { bg: string; fg: string; border: string }

// プロジェクト計画のステータスセレクトと同じ配色（WbsItemStatus）
const WBS_STATUS_STYLES: Record<string, ChipStyle> = {
  [WbsItemStatus.TODO]: {
    bg: colorPalette.monotone[1],
    fg: colorPalette.monotone[4],
    border: colorPalette.monotone[2],
  },
  [WbsItemStatus.DOING]: {
    bg: colorPalette.yellow[1],
    fg: colorPalette.yellow[4],
    border: colorPalette.yellow[2],
  },
  [WbsItemStatus.REVIEW]: {
    bg: colorPalette.green[1],
    fg: colorPalette.green[7],
    border: colorPalette.green[2],
  },
  [WbsItemStatus.DONE]: {
    bg: colorPalette.skyBlue[1],
    fg: colorPalette.skyBlue[7],
    border: colorPalette.skyBlue[2],
  },
  [WbsItemStatus.DISCARD]: {
    bg: colorPalette.monotone[3],
    fg: colorPalette.monotone[0],
    border: colorPalette.monotone[3],
  },
}

// プロジェクト/プログラム/グループ行のステータス → 配色を流用する WbsItemStatus。
// 色: STANDBY=ToDo / INPROGRESS=Doing / FINISHED=Done / CANCELED=Discard
const PROJECT_STATUS_TO_WBS_STATUS: Record<string, WbsItemStatus> = {
  STANDBY: WbsItemStatus.TODO,
  INPROGRESS: WbsItemStatus.DOING,
  FINISHED: WbsItemStatus.DONE,
  CANCELED: WbsItemStatus.DISCARD,
}

const DEFAULT_STYLE: ChipStyle = {
  bg: colorPalette.monotone[0],
  fg: colorPalette.monotone[10],
  border: colorPalette.monotone[10],
}

const resolveStyle = (status: string): ChipStyle => {
  const own = WBS_STATUS_STYLES[status]
  if (own) return own
  const mapped = PROJECT_STATUS_TO_WBS_STATUS[status]
  if (mapped) return WBS_STATUS_STYLES[mapped]
  return DEFAULT_STYLE
}

// WbsItemStatus は ToDo/Doing/... のラベル、プロジェクト系ステータスは値をそのまま表示する
const resolveLabel = (status: string): string => {
  for (const value of Object.values(WbsItemStatus)) {
    if (value === status) return getWbsItemStatusLabel(value) ?? status
  }
  return status
}

type StatusChipProps = {
  status?: string | null
  // セレクトボックス風に ▼ アイコンを表示するか（既定: false。表示専用のため通常は不要）
  showChevron?: boolean
}

/**
 * プロジェクト計画のステータスセレクトと同じ見た目の表示専用チップ。
 * WbsItemStatus（工程/ワークグループ）と プロジェクト系ステータス
 * （STANDBY/INPROGRESS/FINISHED/CANCELED）の両方を色付きで表示する。
 */
export const StatusChip = ({
  status,
  showChevron = false,
}: StatusChipProps) => {
  if (!status) return null
  const style = resolveStyle(status)
  return (
    <Chip bg={style.bg} fg={style.fg} bd={style.border}>
      <Label>{resolveLabel(status)}</Label>
      {showChevron && <ChevronIcon />}
    </Chip>
  )
}

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ flexShrink: 0, color: colorPalette.monotone[4] }}
  >
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </svg>
)

const Chip = styled('span', {
  shouldForwardProp: prop => prop !== 'bg' && prop !== 'fg' && prop !== 'bd',
})<{ bg: string; fg: string; bd: string }>(({ bg, fg, bd }) => ({
  display: 'inline-flex',
  // 通常は 77px の固定幅だが、列を縮めた際はセル幅まで縮む（ラベルは省略表示）
  minWidth: 'min(77px, 100%)',
  maxWidth: '100%',
  height: 22,
  padding: '4px 6px 4px 8px',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 4,
  boxSizing: 'border-box',
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '100%',
  boxShadow: `0px 1px 2px 0px ${colorPalette.monotone[4]}4D`,
  backgroundColor: bg,
  color: fg,
  border: `1px solid ${bd}`,
}))

const Label = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})
